import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Palette } from "@phosphor-icons/react";
import LoadFailedPanel from "@/components/LoadFailedPanel";

export default function OrgBranding() {
  const { user } = useAuth();
  const can = (user?.ux_tier || 0) >= 5;
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadErr(null);
    try { const { data } = await api.get("/admin/branding"); setB(data); }
    catch (e) { setLoadErr(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await api.put("/admin/branding", {
        primary_color: b.primary_color,
        accent_color: b.accent_color,
        footer_legal: b.footer_legal,
      });
      setMsg("Branding saved. (Reload to see changes everywhere.)");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Save failed."); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB({...b, logo_url: data.logo_url}); setMsg("Logo uploaded.");
    } catch (er) { setErr(formatApiError(er.response?.data?.detail) || "Upload failed."); }
  };

  if (!can) return <div className="p-6 text-[var(--text-secondary)]">Branding is CEO-only.</div>;
  if (loadErr) return <div className="p-6"><LoadFailedPanel error={loadErr} resource="Org Branding" onRetry={load} /></div>;
  if (!b) return <div className="p-6">Loading…</div>;

  return (
    <div data-testid="branding-page" className="p-6 max-w-3xl space-y-5">
      <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
        <Palette size={28} className="text-[#F47D20]" /> Organisation Branding
      </h1>
      <div className="bg-white border border-[var(--border)] rounded p-5 space-y-4">
        <div className="flex items-start gap-5">
          {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-20 border border-[var(--border)] rounded p-2" /> :
            <div className="h-20 w-32 bg-[var(--surface-3)] rounded flex items-center justify-center text-[var(--text-faint)] text-xs">No logo</div>}
          <label className="cursor-pointer text-[#263776] hover:underline text-sm">
            <input data-testid="branding-logo-upload" type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={uploadLogo} className="hidden" />
            Upload logo
          </label>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-[var(--text-muted)] mb-1">Primary color</label>
            <input data-testid="branding-primary-color" type="color" value={b.primary_color}
              onChange={e => setB({...b, primary_color: e.target.value})}
              className="w-full h-9 border border-[var(--border-strong)] rounded" />
            <input value={b.primary_color} onChange={e => setB({...b, primary_color: e.target.value})}
              className="mt-1 w-full border border-[var(--border-strong)] rounded px-2 py-1 text-xs" />
          </div>
          <div>
            <label className="block text-xs text-[var(--text-muted)] mb-1">Accent color</label>
            <input data-testid="branding-accent-color" type="color" value={b.accent_color}
              onChange={e => setB({...b, accent_color: e.target.value})}
              className="w-full h-9 border border-[var(--border-strong)] rounded" />
            <input value={b.accent_color} onChange={e => setB({...b, accent_color: e.target.value})}
              className="mt-1 w-full border border-[var(--border-strong)] rounded px-2 py-1 text-xs" />
          </div>
        </div>
        <div>
          <label className="block text-xs text-[var(--text-muted)] mb-1">Footer legal text</label>
          <textarea data-testid="branding-footer" rows={2} value={b.footer_legal}
            onChange={e => setB({...b, footer_legal: e.target.value})}
            className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
        </div>
        <button data-testid="branding-save" onClick={save} disabled={busy}
          className="bg-[#F47D20] text-white px-5 py-2 text-sm rounded hover:bg-[#c96009] disabled:opacity-50">
          {busy ? "Saving…" : "Save branding"}
        </button>
        {msg && <span data-testid="branding-msg" className="ml-3 text-emerald-600 text-sm">{msg}</span>}
        {err && <span data-testid="branding-err" className="ml-3 text-rose-600 text-sm">{err}</span>}
      </div>
    </div>
  );
}
